import {
  formatMinor,
  HST_PERIOD_STATUS_LABELS,
  type HstReturn,
  type ReturnDetailRow,
} from '@hvac/contracts';
import { Alert, Button, Card, CardBody, CardHeader, Pill, Skeleton } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  buildReturn,
  closeKeys,
  returnDetailQuery,
  returnsQuery,
} from '../../shared/api/close-queries.js';

/**
 * The Return.
 *
 * The GST34 as the form actually reads, by its own line numbers, because the
 * person checking it has the CRA's page open beside this one and matching
 * figures to invented labels is the work we are trying to remove.
 *
 * Every computed line opens. That is the whole design: a total nobody can
 * click into is a number they have to trust, and nobody signs a return on
 * trust. Opening a line lists the ledger lines behind it, each naming its
 * supplier and carrying its document, and the detail adds to the figure above
 * it because both come from the same query.
 */

interface LineSpec {
  readonly line: '101' | '105' | '108' | '109';
  readonly label: string;
  readonly meaning: string;
  readonly openable: boolean;
}

const LINES: readonly LineSpec[] = [
  {
    line: '101',
    label: 'Sales and other revenue',
    meaning: 'The work invoiced this period, before tax.',
    openable: true,
  },
  {
    line: '105',
    label: 'Total GST/HST collected',
    meaning: 'Tax charged to customers and held on the government’s behalf.',
    openable: true,
  },
  {
    line: '108',
    label: 'Total input tax credits',
    meaning: 'Tax paid on purchases that the engine decided is claimable.',
    openable: true,
  },
  {
    line: '109',
    label: 'Net tax',
    meaning: 'Line 105 less line 108. What is owed, or refunded.',
    openable: false,
  },
];

function amountFor(row: HstReturn, line: LineSpec['line']): string {
  switch (line) {
    case '101':
      return row.line101Sales;
    case '105':
      return row.line105TotalTax;
    case '108':
      return row.line108TotalItc;
    case '109':
      return row.line109NetTax;
  }
}

export function ReturnPanel({ organizationId }: { organizationId: string }) {
  const queryClient = useQueryClient();
  const returns = useQuery(returnsQuery(organizationId));
  const [openLine, setOpenLine] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const current = returns.data?.[0] ?? null;

  const rebuild = useMutation({
    mutationFn: () => buildReturn(organizationId, current?.id ?? ''),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: closeKeys.all(organizationId) });
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'That return could not be worked out.'),
  });

  if (returns.isPending) return <Skeleton className="h-48 w-full" />;

  if (current === null) {
    return (
      <Card>
        <CardHeader
          title="The return"
          description="No filing periods yet. They are created once per year, from the company's filing frequency."
        />
      </Card>
    );
  }

  const isFiled = current.status === 'filed' || current.status === 'closed';
  const owing = BigInt(current.line109NetTax);

  return (
    <Card>
      <CardHeader
        title={`GST/HST, ${current.periodStart} to ${current.periodEnd}`}
        description={
          isFiled
            ? `Filed${current.filedOn === null ? '' : ` on ${current.filedOn}`}. A filed return is a statement of what was submitted, so it no longer changes.`
            : current.filingDueOn === null
              ? 'Worked out from the ledger. Every figure opens.'
              : `Due ${current.filingDueOn}. Worked out from the ledger, and every figure opens.`
        }
        actions={
          <Pill tone={isFiled ? 'success' : 'neutral'}>
            {HST_PERIOD_STATUS_LABELS[current.status]}
          </Pill>
        }
      />
      <CardBody className="flex flex-col gap-3">
        {error === null ? null : (
          <Alert tone="danger" title="That did not happen">
            {error}
          </Alert>
        )}

        <div className="flex flex-col">
          {LINES.map((spec) => {
            const isOpen = openLine === spec.line;
            return (
              <div key={spec.line} className="border-b border-border last:border-0">
                <div className="flex items-baseline gap-3 py-3">
                  <span className="w-10 shrink-0 text-xs text-content-subtle tabular">
                    {spec.line}
                  </span>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-content">{spec.label}</p>
                    <p className="text-xs text-content-muted">{spec.meaning}</p>
                  </div>
                  <span
                    className={[
                      'tabular text-sm',
                      spec.line === '109' ? 'font-semibold text-content' : 'text-content',
                    ].join(' ')}
                  >
                    {formatMinor(amountFor(current, spec.line), current.currencyCode)}
                  </span>
                  {spec.openable ? (
                    <button
                      type="button"
                      onClick={() => setOpenLine(isOpen ? null : spec.line)}
                      aria-expanded={isOpen}
                      className="shrink-0 text-xs text-brand-600 hover:text-brand-700"
                    >
                      {isOpen ? 'Hide' : 'Open'}
                    </button>
                  ) : (
                    <span className="w-8 shrink-0" />
                  )}
                </div>

                {isOpen ? (
                  <LineDetail
                    organizationId={organizationId}
                    returnId={current.id}
                    line={spec.line}
                    currencyCode={current.currencyCode}
                  />
                ) : null}
              </div>
            );
          })}
        </div>

        <p className="text-sm text-content-muted">
          {owing > 0n
            ? `This period owes ${formatMinor(current.line109NetTax, current.currencyCode)}.`
            : owing === 0n
              ? 'This period owes nothing.'
              : `This period is owed a refund of ${formatMinor((-owing).toString(), current.currencyCode)}.`}
        </p>

        {isFiled ? null : (
          <div className="flex items-center gap-2 border-t border-border pt-4">
            <Button
              variant="secondary"
              onClick={() => rebuild.mutate()}
              isLoading={rebuild.isPending}
            >
              Work it out again
            </Button>
            <span className="text-xs text-content-muted">
              Filing is done in the CRA’s own portal. Record it here afterwards and the figures
              freeze.
            </span>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function LineDetail({
  organizationId,
  returnId,
  line,
  currencyCode,
}: {
  organizationId: string;
  returnId: string;
  line: string;
  currencyCode: string;
}) {
  const detail = useQuery(returnDetailQuery(organizationId, returnId, line));

  if (detail.isPending) return <Skeleton className="mb-3 h-20 w-full" />;
  if (detail.isError) {
    return (
      <p className="pb-3 text-sm text-danger">
        {detail.error instanceof Error ? detail.error.message : 'That detail could not be loaded.'}
      </p>
    );
  }

  const rows = detail.data;
  if (rows.length === 0) {
    return <p className="pb-3 text-sm text-content-muted">Nothing contributed to this figure.</p>;
  }

  const unverified = rows.filter((row) => !row.fromVerifiedRule).length;

  return (
    <div className="animate-rise mb-3 overflow-x-auto rounded-[var(--radius-control)] bg-surface-sunken">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-content-muted">
            <th className="px-3 py-2 text-left font-normal">Date</th>
            <th className="px-3 py-2 text-left font-normal">Supplier</th>
            <th className="px-3 py-2 text-left font-normal">What</th>
            <th className="px-3 py-2 text-right font-normal">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <DetailRow key={row.transactionLineId} row={row} line={line} currency={currencyCode} />
          ))}
        </tbody>
      </table>
      {unverified === 0 ? null : (
        <p className="px-3 py-2 text-xs text-warning">
          {unverified} of these rest on a rule set nobody has checked against the CRA.
        </p>
      )}
    </div>
  );
}

function DetailRow({
  row,
  line,
  currency,
}: {
  row: ReturnDetailRow;
  line: string;
  currency: string;
}) {
  const amount =
    line === '108' || line === '106'
      ? row.itcAmountMinor
      : line === '105' || line === '103'
        ? row.taxAmountMinor
        : row.netAmountMinor;

  return (
    <tr className="border-t border-border">
      <td className="px-3 py-2 tabular text-content-muted">{row.occurredOn}</td>
      <td className="px-3 py-2 text-content">{row.supplier}</td>
      <td className="px-3 py-2 text-content-muted">
        {row.sourceDocumentId === null ? (
          (row.description ?? 'No description')
        ) : (
          <Link
            to="/documents/$documentId"
            params={{ documentId: row.sourceDocumentId }}
            className="text-brand-600 hover:text-brand-700"
          >
            {row.description ?? 'See the document'}
          </Link>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular text-content">{formatMinor(amount, currency)}</td>
    </tr>
  );
}
